/**
 * Threat form spec — drives the shared FormModal for the Threat library tab.
 *
 * Threat is the one entity with a derived, live feasibility score, so its spec
 * uses FormModal's `custom` field type to render a preview that recomputes from
 * the 5 feasibility-factor selects as the user changes them. The ISO 21434
 * attack-feasibility option tables (code → points) + rating thresholds live
 * here; `toPayload` rebuilds the same `entity_data` shape the old bespoke
 * ThreatForm produced.
 */
import React from 'react';
import { TypographyMuted } from '../../../components/ui/Typography';

const STRIDE_CATEGORIES = [
  'SPOOFING',
  'TAMPERING',
  'REPUDIATION',
  'INFORMATION_DISCLOSURE',
  'DENIAL_OF_SERVICE',
  'ELEVATION_OF_PRIVILEGE',
];

const SECURITY_PROPERTIES = [
  'Confidentiality',
  'Integrity',
  'Availability',
  'Authenticity',
  'Non-repudiation',
  'Authorization',
];

const ELAPSED_TIME_OPTIONS = [
  { code: 'ET0', label: '<1 day', points: 0 },
  { code: 'ET1', label: '<1 week', points: 1 },
  { code: 'ET2', label: '<1 month', points: 4 },
  { code: 'ET3', label: '<6 months', points: 17 },
  { code: 'ET4', label: '>6 months', points: 19 },
];
const SPECIALIST_EXPERTISE_OPTIONS = [
  { code: 'SE0', label: 'Layman', points: 0 },
  { code: 'SE1', label: 'Proficient', points: 3 },
  { code: 'SE2', label: 'Expert', points: 6 },
  { code: 'SE3', label: 'Multiple experts', points: 8 },
];
const KNOWLEDGE_OPTIONS = [
  { code: 'KoIC0', label: 'Public', points: 0 },
  { code: 'KoIC1', label: 'Restricted', points: 3 },
  { code: 'KoIC2', label: 'Confidential', points: 7 },
  { code: 'KoIC3', label: 'Strictly confidential', points: 11 },
];
const WINDOW_OPTIONS = [
  { code: 'WoO0', label: 'Unlimited', points: 0 },
  { code: 'WoO1', label: 'Easy', points: 1 },
  { code: 'WoO2', label: 'Moderate', points: 4 },
  { code: 'WoO3', label: 'Difficult/none', points: 10 },
];
const EQUIPMENT_OPTIONS = [
  { code: 'Eq0', label: 'Standard', points: 0 },
  { code: 'Eq1', label: 'Specialized', points: 4 },
  { code: 'Eq2', label: 'Bespoke', points: 7 },
  { code: 'Eq3', label: 'Multiple bespoke', points: 9 },
];

const FEASIBILITY_FACTORS_CONFIG = [
  { key: 'ET', label: 'Elapsed Time', options: ELAPSED_TIME_OPTIONS },
  { key: 'SE', label: 'Specialist Expertise', options: SPECIALIST_EXPERTISE_OPTIONS },
  { key: 'KoIC', label: 'Knowledge of Item/Component', options: KNOWLEDGE_OPTIONS },
  { key: 'WoO', label: 'Window of Opportunity', options: WINDOW_OPTIONS },
  { key: 'Eq', label: 'Equipment', options: EQUIPMENT_OPTIONS },
];

// Option code → points (codes are unique across all factors, e.g. ET0, SE0).
const SCORE_BY_CODE = Object.fromEntries(
  FEASIBILITY_FACTORS_CONFIG.flatMap((c) => c.options.map((o) => [o.code, o.points])),
);

// 2.0 Select forbids empty-string item values, so "not set" uses a sentinel.
const NOT_SET = '__not_set__';
const feasFieldName = (key) => `feas_${key}`;

function computeRating(total) {
  if (total <= 9) return 'High';
  if (total <= 13) return 'Medium';
  if (total <= 19) return 'Low';
  return 'Very Low';
}

// Walk the 5 feasibility selects in `values` → { factors, total, rating }.
// total/rating are null when nothing is set (matches the old bespoke form).
function computeFeasibility(values) {
  const factors = [];
  for (const config of FEASIBILITY_FACTORS_CONFIG) {
    const code = values[feasFieldName(config.key)];
    if (!code || code === NOT_SET) continue;
    factors.push({ category: config.key, optionCode: code, rationale: '' });
  }
  if (factors.length === 0) return { factors, total: null, rating: null };
  const total = factors.reduce((s, f) => s + (SCORE_BY_CODE[f.optionCode] ?? 0), 0);
  return { factors, total, rating: computeRating(total) };
}

export const THREAT_SPEC = {
  label: 'Threat',
  fields: (refs) => [
    { name: 'name', type: 'text', label: 'Threat Name', required: true, autoFocus: true },
    { name: 'threatId', type: 'text', label: 'Threat ID', placeholder: 'e.g. THREAT-001' },
    { name: 'description', type: 'textarea', label: 'Description', rows: 3 },
    {
      name: 'strideCategory',
      type: 'select',
      label: 'STRIDE Category',
      required: true,
      options: STRIDE_CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') })),
    },
    {
      name: 'securityProperties',
      type: 'multi-select',
      label: 'Security Properties',
      options: SECURITY_PROPERTIES.map((p) => ({ value: p, label: p })),
    },
    {
      name: 'technologyRefs',
      type: 'multi-select',
      label: 'Technology References',
      options: (refs.technologies || []).map((t) => ({ value: t.item_id, label: t.name })),
    },
    {
      name: 'threatScenario',
      type: 'textarea',
      label: 'Threat Scenario',
      required: true,
      rows: 4,
      placeholder: 'Describe the threat scenario in detail...',
    },
    ...FEASIBILITY_FACTORS_CONFIG.map((config) => ({
      name: feasFieldName(config.key),
      type: 'select',
      label: config.label,
      placeholder: '— Not set —',
      options: [
        { value: NOT_SET, label: '— Not set —' },
        ...config.options.map((o) => ({ value: o.code, label: `${o.label} (${o.points} pts)` })),
      ],
    })),
    {
      name: 'feasibilityPreview',
      type: 'custom',
      render: (values) => {
        const { total, rating } = computeFeasibility(values);
        if (total === null) return null;
        return (
          <TypographyMuted>
            Total: {total} pts — Rating: <strong>{rating}</strong>
          </TypographyMuted>
        );
      },
    },
  ],
  initialValues: (item) => {
    const ed = item?.entity_data || {};
    const feasInit = {};
    for (const config of FEASIBILITY_FACTORS_CONFIG) {
      const existing = (ed.feasibilityFactors || []).find((f) => f.category === config.key);
      feasInit[feasFieldName(config.key)] = existing?.optionCode || NOT_SET;
    }
    return {
      name: item?.name || '',
      threatId: ed.threatId || '',
      description: item?.description || '',
      strideCategory: ed.strideCategory || 'SPOOFING',
      securityProperties: ed.securityProperties || [],
      technologyRefs: ed.technologyRefs || [],
      threatScenario: ed.threatScenario || '',
      ...feasInit,
    };
  },
  toPayload: (v) => {
    const { factors, total, rating } = computeFeasibility(v);
    return {
      name: v.name,
      description: v.description,
      entity_type: 'threat',
      entity_data: {
        threatId: v.threatId,
        strideCategory: v.strideCategory,
        technologyRefs: v.technologyRefs,
        securityProperties: v.securityProperties,
        threatScenario: v.threatScenario,
        feasibilityFactors: factors,
        feasibilityTotal: total,
        feasibilityRating: rating,
      },
    };
  },
};
